import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Menu, X, ArrowRight, ChevronDown, LayoutDashboard, ShieldCheck, LogOut, MessageCircle, UserRound } from "lucide-react";
import { TOOLS } from "@/lib/toolsContent";
import { useState, useEffect } from "react";
import NotificationBell from "@/components/social/NotificationBell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { toast } from "sonner";
import VisitTracker from "@/components/VisitTracker";

function FooterSignup() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/signup", { email, source: "footer" });
      toast.success(data.already_subscribed ? "You're already on the list — thank you." : "You're in. Watch your inbox.");
      setEmail("");
    } catch {
      toast.error("Could not sign up. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={onSubmit} className="flex gap-2 mt-3" data-testid="footer-newsletter-form">
      <input
        type="email"
        required
        placeholder="your@email.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid="footer-newsletter-email"
        className="flex-1 px-3 py-2 text-sm outline-none"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
      />
      <button type="submit" disabled={busy} data-testid="footer-newsletter-submit" className="btn-primary disabled:opacity-50" style={{ padding: "0.6rem 1rem" }}>
        {busy ? "…" : <ArrowRight size={14} />}
      </button>
    </form>
  );
}

const navItems = [
  { to: "/tools/tracker", label: "Tracker" },
  { to: "/tools/attendance", label: "Attendance" },
  { to: "/tools/prayer", label: "Prayer" },
  { to: "/tools/kiosk", label: "Kiosk" },
  { to: "/tools/social", label: "Social" },
  { to: "/pricing", label: "Pricing" },
];

const ministryItems = [
  { to: "/blog", label: "Journal" },
  { to: "/books", label: "Books" },
  { to: "/read-with-me", label: "Read With Me" },
  { to: "/counseling", label: "Counseling" },
  { to: "/about", label: "About" },
  { to: "/membership", label: "Members" },
];

const slugify = (l) => l.toLowerCase().replace(/\s+/g, "-");

const TOOL_BLURBS = {
  tracker: "Walk guests from first visit to graduation",
  attendance: "Sections, leaders and QR check-in",
  prayer: "A one-tap prayer log for your church",
  kiosk: "A self-serve lobby tablet",
  social: "Your church's own free social feed",
};

const menuStyle = { background: "var(--bg-alt)", borderColor: "var(--border-strong)", color: "var(--ink)" };
const triggerCls = "inline-flex items-center gap-1 text-sm tracking-wide outline-none transition-colors hover:text-[var(--ink)] data-[state=open]:text-[var(--ink)]";

function ToolsMenu() {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className={triggerCls} style={{ color: "var(--ink-2)" }} data-testid="nav-tools-trigger">
        Tools <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={12} className="w-[34rem] p-3" style={menuStyle} data-testid="nav-tools-panel">
        <div className="grid grid-cols-2 gap-1">
          {TOOLS.map((t) => (
            <DropdownMenuItem key={t.slug} asChild>
              <Link to={`/tools/${t.slug}`} className="cursor-pointer flex items-start gap-3 p-3 rounded-md focus:bg-[var(--surface-hover)]" data-testid={`nav-tools-${t.slug}`}>
                <span className="mt-1.5 w-2 h-2 rounded-full shrink-0" style={{ background: t.accent, boxShadow: `0 0 0 4px ${t.glow}` }} />
                <span className="min-w-0">
                  <span className="block font-serif-display text-base leading-tight" style={{ color: "var(--ink)" }}>{t.name}</span>
                  <span className="block text-xs mt-0.5 leading-snug" style={{ color: "var(--muted)" }}>{TOOL_BLURBS[t.slug]}</span>
                </span>
              </Link>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem asChild>
            <Link to="/demo" className="cursor-pointer flex items-center justify-between gap-2 p-3 rounded-md focus:bg-[var(--surface-hover)]" style={{ border: "1px dashed var(--border-strong)" }} data-testid="nav-tools-demo">
              <span className="text-sm" style={{ color: "var(--gold)" }}>Request a demo</span>
              <ArrowRight size={14} style={{ color: "var(--gold)" }} />
            </Link>
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function AccountMenu({ user, onLogout }) {
  const initials = (user.name || user.email || "?").split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const item = "cursor-pointer flex items-center gap-2 focus:bg-[var(--surface-hover)]";
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="w-9 h-9 rounded-full flex items-center justify-center font-serif-display text-sm outline-none transition-transform hover:scale-105" style={{ background: "var(--bg-deep)", color: "var(--gold)", border: "1.5px solid var(--gold)" }} aria-label="Account" data-testid="nav-account-trigger">
        {initials}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-56" style={menuStyle} data-testid="nav-account-menu">
        <div className="px-2 py-2">
          <div className="text-sm font-medium truncate" style={{ color: "var(--ink)" }}>{user.name || "Signed in"}</div>
          <div className="text-xs truncate" style={{ color: "var(--muted)" }}>{user.email}</div>
        </div>
        <DropdownMenuSeparator style={{ background: "var(--border-subtle)" }} />
        <DropdownMenuItem asChild><Link to={`/social/u/${user.id}`} className={item} data-testid="nav-my-profile-link"><UserRound size={14} /> My profile</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/dashboard" className={item} data-testid="nav-dashboard-link"><LayoutDashboard size={14} /> Dashboard</Link></DropdownMenuItem>
        {user.is_admin && <DropdownMenuItem asChild><Link to="/admin" className={item} style={{ color: "var(--ember)" }} data-testid="nav-admin-link"><ShieldCheck size={14} /> Admin</Link></DropdownMenuItem>}
        <DropdownMenuSeparator style={{ background: "var(--border-subtle)" }} />
        <DropdownMenuItem onSelect={onLogout} className={item} data-testid="logout-button"><LogOut size={14} /> Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MinistryMenu() {
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className={triggerCls} style={{ color: "var(--ink-2)" }} data-testid="nav-ministry-trigger">
        Ministry <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={12} className="w-56" style={menuStyle} data-testid="nav-ministry-panel">
        <DropdownMenuItem asChild><Link to="/ministry" data-testid="nav-ministry-overview" className="cursor-pointer font-medium" style={{ color: "var(--gold)" }}>Ministry overview</Link></DropdownMenuItem>
        <DropdownMenuSeparator style={{ background: "var(--border-subtle)" }} />
        {ministryItems.map((m) => (
          <DropdownMenuItem key={m.to} asChild>
            <Link to={m.to} className="cursor-pointer" data-testid={`nav-ministry-${slugify(m.label)}`}>{m.label}</Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--bg-base)" }}>
      <VisitTracker />
      <header className="border-b" style={{ borderColor: "var(--border-subtle)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between gap-6">
          <Link to="/" data-testid="nav-brand-link" className="inline-flex items-center gap-3 group">
            <span
              className="hidden sm:inline-flex items-center justify-center rounded-full font-serif-display uppercase text-xs tracking-[0.24em] transition-colors"
              style={{
                background: "var(--bg-deep)",
                color: "var(--gold)",
                border: "1.5px solid var(--gold)",
                boxShadow: "0 0 0 3px var(--bg-base), 0 8px 22px -12px rgba(230, 184, 92, 0.5)",
                width: "2.75rem",
                height: "2.75rem",
                letterSpacing: "0",
                fontSize: "0.95rem",
              }}
              aria-hidden="true"
            >
              tdl
            </span>
            <span
              className="inline-flex flex-col items-start px-4 py-2 rounded-md"
              style={{
                background: "var(--bg-deep)",
                border: "1.5px solid var(--gold)",
                boxShadow: "inset 0 0 0 3px var(--bg-base), 0 6px 18px -12px rgba(0,0,0,0.5)",
              }}
            >
              <span className="font-serif-display text-xl lg:text-2xl tracking-[-0.02em] lowercase leading-none" style={{ color: "var(--ink)" }}>
                the<span style={{ color: "var(--gold)" }}>disciple.life</span>
              </span>
              <span className="text-[0.55rem] uppercase tracking-[0.32em] mt-1.5 font-semibold" style={{ color: "var(--gold)" }}>
                Equip · Disciple · Grow
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center">
            <ToolsMenu />
            <NavLink to="/pricing" data-testid="nav-pricing-link" className={({ isActive }) => `text-sm tracking-wide transition-colors hover:text-[var(--ink)] ${isActive ? "font-semibold" : ""}`} style={({ isActive }) => ({ color: isActive ? "var(--ember)" : "var(--ink-2)" })}>Pricing</NavLink>
            <MinistryMenu />
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <>
                <NotificationBell />
                <Link to="/social" data-testid="nav-my-social-link" className="inline-flex items-center gap-1.5 text-sm px-3.5 py-1.5 rounded-full transition-colors hover:bg-[rgba(167,139,250,0.22)]" style={{ color: "#C4B5FD", background: "rgba(167,139,250,0.12)", border: "1px solid rgba(167,139,250,0.35)" }}>
                  <MessageCircle size={14} /> My feed
                </Link>
                <AccountMenu user={user} onLogout={handleLogout} />
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link" className="text-sm transition-colors hover:text-[var(--ink)]" style={{ color: "var(--ink-2)" }}>Sign in</Link>
                <Link to="/demo" data-testid="nav-demo-btn" className="btn-primary" style={{ padding: "0.55rem 1rem" }}>Request a demo</Link>
              </>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-2">
          {user && <NotificationBell testid="notif-bell-mobile" />}
          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t px-6 py-4 flex flex-col gap-3" style={{ borderColor: "var(--border-subtle)" }}>
            {user && <Link to="/social" onClick={() => setOpen(false)} className="text-base" style={{ color: "#C4B5FD" }} data-testid="nav-mobile-my-feed">My feed</Link>}
            {user && <Link to={`/social/u/${user.id}`} onClick={() => setOpen(false)} className="text-base" style={{ color: "var(--ink-2)" }} data-testid="nav-mobile-my-profile">My profile</Link>}
            <div className="overline" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Tools</div>
            {navItems.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-mobile-${n.label.toLowerCase()}-link`}
                onClick={() => setOpen(false)}
                className={n.label === "Pricing" ? "text-base" : "text-base pl-3"}
                style={{ color: "var(--ink-2)" }}
              >
                {n.label}
              </NavLink>
            ))}
            <div className="overline mt-2" style={{ color: "var(--gold)", fontSize: "0.6rem" }}>Ministry</div>
            {[{ to: "/ministry", label: "Overview" }, ...ministryItems].map((m) => (
              <Link key={m.to} to={m.to} onClick={() => setOpen(false)} className="text-base pl-3" style={{ color: "var(--ink-2)" }} data-testid={`nav-mobile-ministry-${slugify(m.label)}`}>{m.label}</Link>
            ))}
            <Link to="/demo" onClick={() => setOpen(false)} className="btn-primary w-fit mt-2" data-testid="nav-mobile-demo-btn">Request a demo</Link>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="text-base" style={{ color: "var(--ink-2)" }}>
                  Dashboard
                </Link>
                {user.is_admin && <Link to="/admin" onClick={() => setOpen(false)} className="text-base" style={{ color: "var(--ember)" }}>Admin</Link>}
                <button onClick={handleLogout} className="btn-ghost w-fit">Sign out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost w-fit">Sign in</Link>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-24 border-t" style={{ borderColor: "var(--border-subtle)", background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="overline mb-3">The Disciple Life</div>
            <p className="font-serif-display text-2xl leading-snug" style={{ color: "var(--ink)" }}>
              A ministry of writing, Bible study, and Discipleship.
            </p>
          </div>
          <div>
            <div className="overline mb-3">Explore</div>
            <ul className="space-y-2 text-sm" style={{ color: "var(--ink-2)" }}>
              {navItems.slice(0, 5).map((n) => <li key={n.to}><Link to={n.to} className="hover:underline">{n.label}</Link></li>)}
              <li><Link to="/ministry" className="hover:underline">Ministry</Link></li>
              <li><Link to="/books" className="hover:underline">Books</Link></li>
              <li><Link to="/demo" className="hover:underline">Request a demo</Link></li>
            </ul>
          </div>
          <div>
            <div className="overline mb-3">Stay in touch</div>
            <p className="text-sm" style={{ color: "var(--ink-2)" }}>
              Get our monthly letter — free essays, scripture, and updates from the ministry. No spam, ever.
            </p>
            <FooterSignup />
          </div>
        </div>
        <div className="border-t" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 text-xs flex flex-wrap items-center justify-between gap-2" style={{ color: "var(--muted)" }}>
            <span>© {new Date().getFullYear()} The Disciple Life — Cody D. Thrash</span>
            <span>Soli Deo Gloria</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
